import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to trigger animations when an element enters the viewport.
 * @param {string} animationClass - Class to apply when visible (default: 'animate-fade-in-up')
 * @param {number} threshold - Intersection threshold (0.0 - 1.0)
 * @param {number} delay - Delay in ms before applying animation
 */
export default function useScrollAnimation(animationClass = 'animate-fade-in-up', threshold = 0.1, delay = 0) {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setTimeout(() => {
                        setIsVisible(true);
                        // Optional: Unobserve after triggering to run only once
                        observer.unobserve(element);
                    }, delay);
                }
            },
            {
                threshold: threshold,
                rootMargin: '50px', // Trigger slightly before element enters
            }
        );

        observer.observe(element);

        return () => {
            if (element) observer.unobserve(element);
        };
    }, [threshold, delay]);

    // Return formatted styling Props
    return {
        ref,
        className: isVisible ? animationClass : 'opacity-0', // Use opacity-0 to hide initially if class not present
        style: { opacity: isVisible ? 1 : 0, transition: 'opacity 0s' } // fallback
    };
}

/**
 * Component-based generic wrapper for easy usage
 */
export const ScrollReveal = ({ children, animation = 'animate-fade-in-up', delay = 0, className = '' }) => {
    const { ref, className: animClass } = useScrollAnimation(animation, 0.1, delay);

    return (
        <div ref={ref} className={`${className} ${animClass}`}>
            {children}
        </div>
    );
};
